/**
 * Matrix Rain Column Engine Hook
 *
 * Manages columns of falling characters with the classic Matrix "digital rain"
 * behavior: varying speeds, trail lengths, and random character mutations.
 */

import { useCallback, useMemo, useRef } from 'react';
import type {
  MatrixColumn,
  MatrixRainConfig,
  MatrixRainIntensity,
} from './types';

const MATRIX_CHARS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function randomChar(): string {
  return MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
}

function getConfig(intensity: MatrixRainIntensity): MatrixRainConfig {
  const configs: Record<MatrixRainIntensity, MatrixRainConfig> = {
    light: {
      columnDensity: 0.4,
      minTrailLength: 4,
      maxTrailLength: 12,
      minSpeed: 0.1,
      maxSpeed: 0.35,
      respawnRate: 0.01,
      mutationRate: 0.02,
    },
    medium: {
      columnDensity: 0.65,
      minTrailLength: 6,
      maxTrailLength: 18,
      minSpeed: 0.15,
      maxSpeed: 0.45,
      respawnRate: 0.02,
      mutationRate: 0.04,
    },
    heavy: {
      columnDensity: 0.85,
      minTrailLength: 8,
      maxTrailLength: 25,
      minSpeed: 0.25,
      maxSpeed: 0.7,
      respawnRate: 0.04,
      mutationRate: 0.06,
    },
  };
  return configs[intensity];
}

function createColumn(
  columnIndex: number,
  config: MatrixRainConfig,
  numRows: number,
  prePopulate: boolean,
): MatrixColumn {
  const trailLength =
    config.minTrailLength +
    Math.floor(Math.random() * (config.maxTrailLength - config.minTrailLength));
  const speed =
    config.minSpeed + Math.random() * (config.maxSpeed - config.minSpeed);
  const chars: string[] = [];
  for (let i = 0; i < trailLength; i++) {
    chars.push(randomChar());
  }

  const headRow = prePopulate
    ? Math.floor(Math.random() * (numRows + trailLength)) - trailLength * 0.3
    : -Math.floor(Math.random() * trailLength);

  return { columnIndex, headRow, trailLength, speed, chars };
}

export interface UseMatrixRainColumnsOptions {
  intensity: MatrixRainIntensity;
  canvasWidth: number;
  canvasHeight: number;
  fontSize: number;
  cellWidth: number;
  cellHeight: number;
}

export interface UseMatrixRainColumnsReturn {
  init: () => void;
  update: () => void;
  getColumns: () => MatrixColumn[];
  reset: () => void;
}

export function useMatrixRainColumns(
  options: UseMatrixRainColumnsOptions,
): UseMatrixRainColumnsReturn {
  const { intensity, canvasWidth, canvasHeight, cellWidth, cellHeight } =
    options;

  const config = useMemo(() => getConfig(intensity), [intensity]);
  const numColumns = Math.max(1, Math.floor(canvasWidth / cellWidth));
  const numRows = Math.max(1, Math.floor(canvasHeight / cellHeight));

  // Track which column slots are active and their state
  const columnsRef = useRef<(MatrixColumn | null)[]>([]);

  const init = useCallback(() => {
    const cols: (MatrixColumn | null)[] = [];
    for (let i = 0; i < numColumns; i++) {
      if (Math.random() < config.columnDensity) {
        cols.push(createColumn(i, config, numRows, true));
      } else {
        cols.push(null);
      }
    }
    columnsRef.current = cols;
  }, [numColumns, numRows, config]);

  const update = useCallback(() => {
    const cols = columnsRef.current;

    for (let i = 0; i < cols.length; i++) {
      const col = cols[i];
      if (col) {
        // Advance head position
        col.headRow += col.speed;

        // Mutate random trail characters
        if (Math.random() < config.mutationRate) {
          const idx = Math.floor(Math.random() * col.chars.length);
          col.chars[idx] = randomChar();
        }

        // Check if the entire trail has moved below the canvas
        const tailRow = col.headRow - col.trailLength;
        if (tailRow >= numRows) {
          cols[i] = null; // Despawn
        }
      } else {
        // Inactive slot: try to respawn
        if (Math.random() < config.respawnRate) {
          cols[i] = createColumn(i, config, numRows, false);
        }
      }
    }

    // Handle resize: add/remove column slots if numColumns changed
    while (cols.length < numColumns) {
      const idx = cols.length;
      cols.push(
        Math.random() < config.respawnRate
          ? createColumn(idx, config, numRows, false)
          : null,
      );
    }
    if (cols.length > numColumns) {
      cols.length = numColumns;
    }
  }, [numColumns, numRows, config]);

  const getColumns = useCallback((): MatrixColumn[] => {
    return columnsRef.current.filter((c): c is MatrixColumn => c !== null);
  }, []);

  const reset = useCallback(() => {
    columnsRef.current = [];
  }, []);

  return useMemo(
    () => ({ init, update, getColumns, reset }),
    [init, update, getColumns, reset],
  );
}
